import React, { useEffect, useState, useRef } from "react";
import DropdownButton from "./DropdownButton";
import ActionButton from "./ActionButton";
import SingleDropdown from "./SingleDropdown";
import { conditionsOperatorObject } from "Static/constant";
import DateRangePicker from "./DateRangePicker";
import { DateUtils } from "Utils/DateUtils";
import contactStoreData from "Stores/contactData";
import Utils from "Utils/utils";

export const ContactDateFilter = props => {
  const {
    label = "",
    keyValue = "",
    activeFilter = "",
    errMsg = "",
    data = [],
    setActiveFilter = () => {},
    setAction = () => {},
    selectedFilterCbk = () => {},
    setErrMsg = () => {},
    // Render date-type + operator on row 1, the date picker on row 2 (opt-in).
    inlineOperator = false
  } = props || {};

  let toggleDropdown = activeFilter === keyValue;

  const [activeDropdownIndex, setActiveDropdownIndex] = useState(false);
  const [dateStyleCss, setDateStyleCss] = useState({});
  // Chip panel: builder hidden behind "+ Add condition" (mirrors other filters).
  const [showBuilder, setShowBuilder] = useState(false);
  // The date picker's built-in Apply button is a one-time DOM listener (stale
  // closure); route it through a ref so it always commits the latest builder row.
  const commitBuilderRef = useRef(() => {});

  const validateFieldNameAlreadyExist = (newFieldName = "") => {
    let count = 0;
    if (data?.length > 1) {
      data.some(element => {
        if (element?.fieldName === newFieldName) {
          count++;
        }
      });
    }
    return !count > 0;
  };

  const handleAction = (refId = "", item = "", index = "") => {
    if (refId === "fieldName") {
      if (!validateFieldNameAlreadyExist(item?.subKey)) {
        return;
      }
      selectedFilterCbk("add", keyValue);
    }
    errMsg && setErrMsg();
    let tempData = [...data];
    tempData[index][refId] = item?.subKey || "";
    if (refId === "operator") {
      let isBetween = item?.subKey === "BETWEEN";
      let isSet = item?.subKey === "IS_SET";
      let isNotSet = item?.subKey === "IS_NOT_SET";
      tempData[index].isNotSet = isBetween ? false : isNotSet;
      tempData[index].isSet = isBetween ? false : isSet;
      if (!isBetween) {
        tempData[index].fromDate = "";
        tempData[index].toDate = "";
      }
    }
    setActiveDropdownIndex(false);
    setAction(keyValue, tempData);
  };

  const handleDateAction = (type = "", index = "", dateObj = {}) => {
    if (type !== "apply") {
      let tempData = [...data];
      const { startDate = new Date(), endDate = null } = dateObj || {};
      tempData[index].fromDate = startDate;
      tempData[index].toDate = endDate;
      setAction(keyValue, tempData);
    }
    if (["apply", "cancel"].includes(type)) {
      setActiveDropdownIndex(false);
      setDateStyleCss({});
    }
    // Picker's Apply commits the chip directly — no separate second Apply.
    if (type === "apply" && inlineOperator) {
      commitBuilderRef.current(index);
    }
    errMsg && setErrMsg("");
  };

  const resetOrAddNewItem = (isPush = false) => {
    let newObj = {
      fieldName: "",
      isSet: false,
      isNotSet: false,
      fromDate: new Date(),
      toDate: null,
      operator: "BETWEEN"
    };
    if (isPush) {
      return newObj;
    }
    errMsg && setErrMsg();
    selectedFilterCbk("remove", keyValue);
    setAction(keyValue, [newObj]);
  };

  const removeItem = index => {
    let tempData = [...data];
    let len = tempData?.length;
    if (len) {
      if (len === 1) {
        errMsg && setErrMsg("");
        resetOrAddNewItem();
      } else {
        tempData.splice(index, 1);
        tempData.filter(item => item?.fieldName)?.length < 1 &&
          selectedFilterCbk("remove", keyValue);
        setAction(keyValue, tempData);
      }
    }
    errMsg && setErrMsg();
  };

  const addItem = () => {
    errMsg && setErrMsg();
    let tempData = [...data];
    tempData.push(resetOrAddNewItem(true));
    setAction(keyValue, tempData);
  };

  const resetItem = () => {
    errMsg && setErrMsg("");
    data?.filter(item => item?.fieldName)?.length && resetOrAddNewItem();
  };

  const optionList = [
    {
      value: "Created",
      subKey: "CREATED",
      key: "created",
      method: handleAction
    },
    {
      value: "Updated",
      subKey: "UPDATED",
      key: "updated",
      method: handleAction
    },
    {
      value: "First contacted",
      subKey: "FIRST_CONTACTED",
      key: "firstContacted",
      method: handleAction
    },
    {
      value: "Last contacted",
      subKey: "LAST_CONTACTED",
      key: "lastContacted",
      method: handleAction
    }
  ];

  const operatorList = [
    {
      value: "<span class='conditionSymbol'>&lt;&gt;</span><span class='conditionLabel'>Between</span>",
      subKey: "BETWEEN",
      key: "between",
      method: handleAction
    },
    {
      value:
        "<span class='conditionSymbol customSymbol' style='font-Size:17px;'>&lcub;&rcub;</span><span class='conditionLabel'>Is set</span>",
      subKey: "IS_SET",
      key: "isSet",
      method: handleAction
    },
    {
      value:
        "<span class='conditionSymbol customSymbol strikeThrough' style='font-Size:17px;'>&lcub;&rcub;</span><span class='conditionLabel'>Is not set</span>",
      subKey: "IS_NOT_SET",
      key: "isNotSet",
      method: handleAction
    }
  ];

  const fieldnameObject = {
    CREATED: "Created",
    UPDATED: "Updated",
    FIRST_CONTACTED: "First contacted",
    LAST_CONTACTED: "Last contacted"
  };

  const toggleDatePicker = (event, index) => {
    let positionY = event.clientY || event.pageY || 0;
    let domPosition =
      30 - event.nativeEvent.layerY || event.nativeEvent.offsetY || 0;
    let innerHeight = window.innerHeight;
    let diffHeight = innerHeight - positionY;
    let styleCss = {};
    if (diffHeight + domPosition >= 366) {
      styleCss.top = positionY + domPosition;
    } else {
      let topHt = positionY - 366;
      styleCss.top = topHt - domPosition;
    }
    setDateStyleCss(styleCss);
    setActiveDropdownIndex(`dateRangePicker-${index}`);
  };

  const formateDate = (fromDate, toDate) => {
    let startDate = DateUtils.formatDateByMonthText(fromDate);
    let endDate = DateUtils.formatDateByMonthText(toDate);
    if (DateUtils.isFromToDateEqual(fromDate, toDate)) {
      return startDate;
    }
    return `${startDate} to ${endDate}`;
  };

  const filterFn = Utils.getFilterFn("CONTACT_DATE_FILTER");

  useEffect(() => {
    if (errMsg) {
      let tempData = { ...contactStoreData.contactFilterObject };
      tempData.dates = [
        {
          fieldName: "",
          isSet: false,
          isNotSet: false,
          fromDate: new Date(),
          toDate: null,
          operator: "BETWEEN"
        }
      ];
      contactStoreData.setContactFilterObject(tempData);
    }
  }, [errMsg]);

  // ──────────────── Chip model (mirrors ContactFieldFilter) ────────────────
  const dateReady = row => {
    if (!row?.fieldName) return false;
    if (row.operator === "IS_SET" || row.isSet) return true;
    if (row.operator === "IS_NOT_SET" || row.isNotSet) return true;
    return !!(row.fromDate && row.toDate);
  };

  const chipRows = inlineOperator ? (data || []).filter(r => r?.committed === true) : [];
  const builderIndex = inlineOperator
    ? (data || []).findIndex(r => r?.committed !== true)
    : -1;

  const chipLabel = item => {
    const f = fieldnameObject?.[item?.fieldName] || item?.fieldName || "";
    if (item?.operator === "IS_SET" || item?.isSet) return `${f} is set`;
    if (item?.operator === "IS_NOT_SET" || item?.isNotSet) return `${f} is not set`;
    if (item?.fromDate && item?.toDate)
      return `${f}: ${formateDate(new Date(item.fromDate), item.toDate)}`;
    return f;
  };

  const removeChip = dataIdx => {
    const rows = [...(data || [])];
    rows.splice(dataIdx, 1);
    if (!rows.some(r => r?.committed === true)) selectedFilterCbk("remove", keyValue);
    setAction(keyValue, rows);
    errMsg && setErrMsg("");
  };

  const commitBuilder = (index = 0) => {
    const rows = [...(data || [])];
    const row = rows[index] || {};
    if (!dateReady(row)) return;
    rows[index] = { ...row, committed: true };
    rows.push({
      fieldName: "",
      isSet: false,
      isNotSet: false,
      fromDate: new Date(),
      toDate: null,
      operator: "BETWEEN",
      committed: false
    });
    selectedFilterCbk("add", keyValue);
    setAction(keyValue, rows);
    setShowBuilder(false);
    errMsg && setErrMsg("");
  };

  // Keep the ref pointing at the latest commit so the picker's Apply commits
  // against current data.
  commitBuilderRef.current = commitBuilder;

  // One-time normalize: complete rows → committed chips + one builder.
  useEffect(() => {
    if (!inlineOperator) return;
    const rows = data || [];
    const committedRows = rows
      .filter(r => dateReady(r))
      .map(r => ({ ...r, committed: true }));
    const next = [
      ...committedRows,
      {
        fieldName: "",
        isSet: false,
        isNotSet: false,
        fromDate: new Date(),
        toDate: null,
        operator: "BETWEEN",
        committed: false
      }
    ];
    if (JSON.stringify(next) !== JSON.stringify(rows)) setAction(keyValue, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="contactDateFilter">
      <DropdownButton
        label={label}
        count={data?.filter(item => item?.fieldName)?.length || 0}
        keyValue={keyValue}
        toggleDropdown={toggleDropdown}
        callback={setActiveFilter}
      />
      {toggleDropdown && (
        <div className="dropdownSearchBox">
          <span className="horizontalBorderLine" />
          {inlineOperator && (
            <div className="filterChipWrap">
              {chipRows.length > 0 && (
                <div className="filterChips">
                  {chipRows.map((c, i) => (
                    <span className="filterChip" key={`dchip-${i}`}>
                      <span className="chipText">{chipLabel(c)}</span>
                      <i
                        className="material-icons chipRemove"
                        onClick={() => removeChip(data.indexOf(c))}
                      >
                        close
                      </i>
                    </span>
                  ))}
                </div>
              )}
              {!showBuilder && (
                <div
                  className="addNewButton chipAddBtn"
                  onClick={() => {
                    setShowBuilder(true);
                    setActiveDropdownIndex(`fieldName-${builderIndex}`);
                  }}
                >
                  <i className="material-icons addIcon">add_circle_outline</i>
                  <span className="text">Add condition</span>
                </div>
              )}
              {showBuilder &&
                builderIndex > -1 &&
                (() => {
                  const index = builderIndex;
                  const bItem = data[index] || {};
                  const bFieldName = bItem.fieldName || "";
                  const bOperator = bItem.operator || "BETWEEN";
                  const bFrom = new Date(bItem.fromDate || new Date());
                  const bTo = bItem.toDate || null;
                  const bNoVal =
                    bOperator === "IS_SET" || bOperator === "IS_NOT_SET";
                  return (
                    <div className="chipBuilder">
                      <div className="fieldnameDropdown fieldnameOperatorRow">
                        <SingleDropdown
                          isDropdownAllowed={true}
                          label={"Select date type"}
                          options={optionList}
                          defaultValue={fieldnameObject?.[bFieldName] || ""}
                          referenceId="fieldName"
                          parentIndex={index}
                          hasCustomFieldValue={true}
                          showDropdown={activeDropdownIndex === `fieldName-${index}`}
                          setActiveDropdownIndex={setActiveDropdownIndex}
                          search={true}
                          filterFn={filterFn}
                          customDropdownClassName="bottomDropdown"
                        />
                        {bFieldName && (
                          <SingleDropdown
                            isDropdownAllowed={
                              !["CREATED", "UPDATED"].includes(bFieldName)
                            }
                            options={operatorList}
                            defaultValue={conditionsOperatorObject?.[bOperator] || ""}
                            showDropdown={activeDropdownIndex === `operator-${index}`}
                            setActiveDropdownIndex={setActiveDropdownIndex}
                            parentIndex={index}
                            referenceId="operator"
                            className="inlineOperatorLabel"
                          />
                        )}
                      </div>
                      {bFieldName && !bNoVal && (
                        <div className="fieldOperatorValue valueOnlyRow">
                          <div className="valueWithApply">
                            <div
                              className={`selectDateRangePicker ${
                                bOperator !== "BETWEEN" ? "disabled" : ""
                              }`}
                            >
                              <div
                                className="selectDateLabel"
                                onClick={event => toggleDatePicker(event, index)}
                              >
                                <span className="title">
                                  {bFrom && bTo
                                    ? formateDate(bFrom, bTo)
                                    : "Choose date"}
                                </span>
                                <i className="material-icons calendarIcon">
                                  date_range
                                </i>
                              </div>
                              {activeDropdownIndex === `dateRangePicker-${index}` &&
                                bOperator === "BETWEEN" && (
                                  <>
                                    <div
                                      className="dropdownOverlay"
                                      onClick={() =>
                                        handleDateAction("cancel", index)
                                      }
                                    />
                                    <div
                                      className="datePickerDropdown"
                                      style={dateStyleCss}
                                    >
                                      <DateRangePicker
                                        index={index}
                                        customDate={[
                                          {
                                            startDate: bFrom,
                                            endDate: bTo,
                                            key: "selection"
                                          }
                                        ]}
                                        pageType="contactFilter"
                                        handleAction={handleDateAction}
                                      />
                                    </div>
                                  </>
                                )}
                            </div>
                          </div>
                        </div>
                      )}
                      {bFieldName && bNoVal && (
                        <div className="fieldOperatorValue valueOnlyRow chipNoValueRow">
                          <span
                            className="applyValueLink"
                            onClick={() => commitBuilder(index)}
                          >
                            Apply
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
          )}
          {!inlineOperator &&
            ((data?.length > 0 && (
            <div className="dateRangeConditionCard">
              {data.map((item, index) => {
                let {
                  fieldName = "",
                  fromDate = new Date(),
                  toDate = null,
                  isSet = false,
                  isNotSet = false,
                  operator = "BETWEEN"
                } = item || {};
                fromDate = new Date(fromDate);

                return (
                  <div
                    className="dateRangeCondition"
                    key={`dateRangeCondition${index}`}
                  >
                    <div
                      className={`fieldnameDropdown ${
                        inlineOperator ? "fieldnameOperatorRow" : ""
                      }`}
                    >
                      <SingleDropdown
                        isDropdownAllowed={true}
                        label={"Select date type"}
                        options={optionList}
                        defaultValue={fieldnameObject?.[fieldName] || ""}
                        referenceId="fieldName"
                        parentIndex={index}
                        hasCustomFieldValue={true}
                        showDropdown={
                          activeDropdownIndex === `fieldName-${index}`
                        }
                        setActiveDropdownIndex={setActiveDropdownIndex}
                        search={true}
                        filterFn={filterFn}
                        customDropdownClassName="bottomDropdown"
                      />
                      {inlineOperator && fieldName && (
                        <SingleDropdown
                          isDropdownAllowed={
                            !["CREATED", "UPDATED"].includes(fieldName)
                          }
                          options={operatorList}
                          defaultValue={
                            conditionsOperatorObject?.[operator] || ""
                          }
                          showDropdown={
                            activeDropdownIndex === `operator-${index}`
                          }
                          setActiveDropdownIndex={setActiveDropdownIndex}
                          parentIndex={index}
                          referenceId="operator"
                          className="inlineOperatorLabel"
                        />
                      )}
                      <i
                        className="material-icons-outlined deleteIcon"
                        onClick={() => removeItem(index)}
                      >
                        delete
                      </i>
                    </div>
                    {fieldName && (
                      <div
                        className={`fieldOperatorValue ${
                          inlineOperator ? "valueOnlyRow" : ""
                        } ${!fieldName ? "disableInput" : ""}`}
                      >
                        {!inlineOperator && (
                          <SingleDropdown
                            isDropdownAllowed={
                              !["CREATED", "UPDATED"].includes(fieldName)
                            }
                            options={operatorList}
                            defaultValue={
                              conditionsOperatorObject?.[operator] || ""
                            }
                            showDropdown={
                              activeDropdownIndex === `operator-${index}`
                            }
                            setActiveDropdownIndex={setActiveDropdownIndex}
                            parentIndex={index}
                            referenceId="operator"
                          />
                        )}
                        <div
                          className={`selectDateRangePicker ${
                            operator !== "BETWEEN" ? "disabled" : ""
                          }`}
                        >
                          <div
                            className="selectDateLabel"
                            onClick={event => toggleDatePicker(event, index)}
                          >
                            <span className="title">
                              {fromDate && toDate
                                ? formateDate(fromDate, toDate)
                                : "Choose date"}
                            </span>
                            <i className="material-icons calendarIcon">
                              date_range
                            </i>
                          </div>
                          {activeDropdownIndex === `dateRangePicker-${index}` &&
                            operator === "BETWEEN" && (
                              <>
                                <div
                                  className="dropdownOverlay"
                                  onClick={() =>
                                    handleDateAction("cancel", index)
                                  }
                                />
                                <div
                                  className="datePickerDropdown"
                                  style={dateStyleCss}
                                >
                                  <DateRangePicker
                                    index={index}
                                    customDate={[
                                      {
                                        startDate: fromDate,
                                        endDate: toDate,
                                        key: "selection"
                                      }
                                    ]}
                                    pageType="contactFilter"
                                    handleAction={handleDateAction}
                                  />
                                </div>
                              </>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )) ||
              "")}
          {!inlineOperator && (
            <ActionButton
              resetCbk={resetItem}
              addCbk={addItem}
              isReset={false}
              addLabel="Add"
            />
          )}
          {!inlineOperator && errMsg === keyValue && (
            <span className="errMessage">Please select a value</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactDateFilter;
